import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import Headline from "~/components/Headline/Headline";
import Hint from "~/components/Hint";
import Modal from "~/components/Modal";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import {
	getInvoiceStatusDisplayNameById,
	invoiceStatusId,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { revertInvoiceStatus } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	processedInvoiceQueries,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { invalidateQuotaQueries } from "~/modules/quota/utils/quotaCacheUtils.ts";

type Props = {
	invoiceData: Invoice;
	isOpen: boolean;
	newStatusId?: InvoiceStatusId
	onClose: () => void;
};

const RevertInvoiceStatusModal = ({
	invoiceData,
	isOpen,
	newStatusId,
	onClose,
}: Props) => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const queryClient = useQueryClient();

	const handleClose = useCallback(() => {
		setServerErrorMessage("");
		setIsBusy(false);
		onClose();
	}, [onClose]);

	const handleConfirmationClick = useCallback(async () => {
		if (!newStatusId) return;
		try {
			setIsBusy(true);
			await revertInvoiceStatus(invoiceData.id, newStatusId);
			queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
			// The quota view shows the status of an advance invoice and of an attached invoice.
			if (invoiceData.quota) void invalidateQuotaQueries(queryClient);
			await queryClient.refetchQueries({ queryKey: processedInvoiceQueries.details(invoiceData.id).queryKey });
			handleClose();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(getValidationMessage(error, ["invoiceId"]) ?? t("form:unexpectedError"));
		} finally {
			setIsBusy(false);
		}

	}, [invoiceData.id, invoiceData.quota, newStatusId, queryClient, handleClose]);


	const currentStatusIsPaid = invoiceData.invoiceStatusId === invoiceStatusId.Paid;
	const currentStatusIdIsSent = invoiceData.invoiceStatusId === invoiceStatusId.SentToClient;

	if (!newStatusId || (!currentStatusIdIsSent && !currentStatusIsPaid)) return null;

	const newStatusIsSent = newStatusId === invoiceStatusId.SentToClient;
	const newStatusDisplayName = getInvoiceStatusDisplayNameById(newStatusId, invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice);
	return (
		<Modal closeOnOutsideClick={true}
			   open={isOpen && !!newStatusId}
			   setOpen={handleClose}
			   widthClassName="w-[48rem]"
		>
			{isBusy && <SidebarBusyOverlay />}
			<div className="flex flex-col gap-y-2">
				<Headline type="h4">Rechnungsstatus auf &quot;{newStatusDisplayName}&quot; zurücksetzen</Headline>
				{!serverErrorMessage && <><p>Beim Zurücksetzen des Status werden folgende
					Informationen <span className="font-bold">unwideruflich gelöscht</span>:</p>
					<ul className="list-inside list-disc">
						{currentStatusIsPaid &&
							<li>Zahlungseingangsdatum</li>}
						{(currentStatusIdIsSent || (currentStatusIsPaid && !newStatusIsSent)) &&
							<li>Versanddatum</li>
						}
					</ul>
					<Hint theme="warning"
						  size="sm">Der Rechnungsstatus sollte generell nur dann zurückgesetzt werden, wenn der aktuelle
						Status fälschlicherweise gesetzt wurde oder nachträgliche Änderungen in Absprache mit dem
						Rechnungsempfänger getätigt werden müssen.</Hint>
				</>}
				{serverErrorMessage && <Hint theme="error"
											 size="sm"
											 title="Statusupdate fehlgeschlagen"><p>{serverErrorMessage}</p></Hint>}
			</div>
			{!serverErrorMessage && <div className="mt-6 flex items-center justify-between">
				<Button onClick={handleConfirmationClick}>Status zurücksetzen</Button>
				<Button theme="white"
						onClick={handleClose}>Abbrechen</Button>
			</div>}
			{serverErrorMessage && <div className="mt-6 flex items-center justify-center">
				<Button onClick={handleClose}>Schließen</Button>
			</div>}
		</Modal>
	);
};

export default RevertInvoiceStatusModal;
